import React from 'react';
import { useIntl } from 'react-intl';

import {
    CSSEditor,
    CSSEditorComponentLabel,
    CSSEditorComponentWrapper,
    CSSEditorWidgetProperties,
} from 'components/Editors/components/Editor.style';
import { EditorColorPicker } from 'components/Editors/components/EditorColorPicker';
import { EditorInput } from 'components/Editors/components/EditorInput';
import { EditorSelect } from 'components/Editors/components/EditorSelect';
import { JRCButton } from 'components/JRCButton/JRCButton';
import { VariantType, VariantSizeType, WidgetButtonProps } from 'jamespot-user-api';

import {
    EditorColorsOptions,
    EditorLinkTargetOptions,
    EditorRadiusOptions,
    EditorSizeOptions,
    EditorVariantOptions,
} from '../const';

/**
 * Default widget button editor
 */
export const JRCWidgetButtonEditor = <T extends {}>({
    text,
    color,
    backgroundColor,
    borderRadius,
    variant,
    buttonSize,
    url,
    target,
    onChange,
    onSave,
    extendProperties,
}: WidgetButtonProps &
    T & {
        onChange: (content: Partial<WidgetButtonProps>) => void;
        onSave: (content?: Partial<WidgetButtonProps>) => void;
        extendProperties?: (onChange: (content: Partial<WidgetButtonProps & T>) => void) => React.ReactNode;
    }) => {
    const intl = useIntl();

    return (
        <CSSEditor>
            <CSSEditorWidgetProperties>
                {extendProperties &&
                    extendProperties((content: Partial<WidgetButtonProps & T>) => {
                        onChange(content);
                    })}
                <CSSEditorComponentWrapper>
                    <EditorInput
                        label={intl.formatMessage({ id: 'PANEL_Text' })}
                        initialValue={text}
                        onChange={(text) => onChange({ text })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <EditorInput
                        label={intl.formatMessage({ id: 'WIDGET_Button_Url' })}
                        initialValue={url ?? ''}
                        onChange={(url) => onChange({ url })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <EditorSelect
                        label={intl.formatMessage({ id: 'WIDGET_Button_Target' })}
                        initialValue={target}
                        options={EditorLinkTargetOptions}
                        onChange={(target) => onChange({ target })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <CSSEditorComponentLabel>{intl.formatMessage({ id: 'PANEL_Text_Color' })}</CSSEditorComponentLabel>
                    <EditorColorPicker
                        colors={EditorColorsOptions}
                        initialValue={color}
                        onChange={(color) => onChange({ color })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <CSSEditorComponentLabel>
                        {intl.formatMessage({ id: 'PANEL_Background_Color' })}
                    </CSSEditorComponentLabel>
                    <EditorColorPicker
                        colors={EditorColorsOptions}
                        initialValue={backgroundColor}
                        onChange={(backgroundColor) => onChange({ backgroundColor })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <EditorSelect
                        label={intl.formatMessage({ id: 'PANEL_Border_Radius' })}
                        options={EditorRadiusOptions}
                        initialValue={borderRadius}
                        onChange={(borderRadius) => onChange({ borderRadius })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <EditorSelect<VariantType>
                        label={intl.formatMessage({ id: 'PANEL_Button_Variant' })}
                        options={EditorVariantOptions}
                        initialValue={variant}
                        onChange={(variant) => onChange({ variant })}
                    />
                </CSSEditorComponentWrapper>
                <CSSEditorComponentWrapper>
                    <EditorSelect<VariantSizeType>
                        label={intl.formatMessage({ id: 'PANEL_Button_Size' })}
                        options={EditorSizeOptions}
                        initialValue={buttonSize}
                        onChange={(buttonSize) => onChange({ buttonSize })}
                    />
                </CSSEditorComponentWrapper>
            </CSSEditorWidgetProperties>
            <JRCButton onClick={() => onSave()} variant={'outlined'} minWidth={'100%'}>
                {intl.formatMessage({ id: 'PANEL_Save_And_Close' })}
            </JRCButton>
        </CSSEditor>
    );
};
